import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, TrendingUp, Star, ShoppingCart, ArrowRight, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

export const PersonalizedRecommendations = ({ onAddToCart }) => {
  const { token, isAuthenticated } = useAuth();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    fetchRecommendations();
  }, [isAuthenticated]);

  const fetchRecommendations = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/recommendations/personalized`, { headers });
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Track product view
  const trackView = async (productId, productType, category) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      await axios.post(`${API_URL}/api/recommendations/track`, {
        product_id: productId,
        product_type: productType,
        category: category,
        action: 'view'
      }, { headers });
    } catch (error) {
      // Silent fail for tracking
    }
  };

  const handleProductClick = (product, type = 'product') => {
    trackView(product.id, type, product.category);
  };

  const handleAddToCart = (product, type = 'product') => {
    trackView(product.id, type, product.category);
    if (onAddToCart) {
      onAddToCart(product, type);
    }
    toast.success(`Added ${product.name} to cart!`);
  };

  if (loading) {
    return (
      <section className="py-8">
        <div className="flex items-center gap-2 mb-6">
          <Skeleton className="h-6 w-6 rounded-full" />
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </section>
    );
  }

  if (!recommendations || !recommendations.products?.length) {
    return null;
  }

  const isPersonalized = recommendations.reason === 'browsing_history';

  return (
    <section className="py-8" data-testid="personalized-recommendations">
      {/* Header with AI message */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isPersonalized 
              ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
              : 'bg-gradient-to-r from-orange-500 to-red-500'
          }`}>
            {isPersonalized ? (
              <Sparkles className="w-5 h-5 text-white" />
            ) : (
              <TrendingUp className="w-5 h-5 text-white" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">
              {isPersonalized ? 'Recommended for You' : 'Popular Picks'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {recommendations.ai_message}
            </p>
          </div>
        </div>
        
        {isPersonalized && recommendations.interests?.categories?.length > 0 && (
          <div className="hidden md:flex items-center gap-2">
            {recommendations.interests.categories.slice(0, 3).map(cat => (
              <Badge 
                key={cat} 
                variant="secondary"
                className="bg-purple-100 text-purple-700 capitalize"
              >
                {cat}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {recommendations.products.slice(0, 8).map((product) => (
          <Card 
            key={product.id}
            className="group relative overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
            onMouseEnter={() => setHoveredId(product.id)}
            onMouseLeave={() => setHoveredId(null)}
            onClick={() => handleProductClick(product)}
            data-testid={`rec-product-${product.id}`}
          >
            {/* Discount Badge */}
            {product.original_price && product.original_price > product.price && (
              <Badge className="absolute top-2 right-2 z-10 bg-red-500 text-white text-xs">
                {Math.round((1 - product.price / product.original_price) * 100)}% OFF
              </Badge>
            )}
            
            {/* AI Pick Badge for personalized */}
            {isPersonalized && (
              <Badge className="absolute top-2 left-2 z-10 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs">
                <Sparkles className="w-3 h-3 mr-1" /> For You
              </Badge>
            )}

            <CardContent className="p-0">
              {/* Image */}
              <div className="relative aspect-square overflow-hidden bg-gray-50">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                />
                
                {/* Hover overlay with actions */}
                <div className={`absolute inset-0 bg-black/40 flex items-center justify-center gap-2 transition-opacity duration-200 ${
                  hoveredId === product.id ? 'opacity-100' : 'opacity-0'
                }`}>
                  <Button
                    size="sm"
                    className="bg-white text-black hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(product);
                    }}
                  >
                    <ShoppingCart className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white/80 border-white"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Heart className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Product Info */}
              <div className="p-3">
                <p className="text-xs text-muted-foreground capitalize mb-1">
                  {product.category}
                </p>
                <h3 className="font-medium text-sm line-clamp-2 mb-2 group-hover:text-cyan-600 transition-colors">
                  {product.name}
                </h3>
                
                {/* Rating */}
                {product.rating && (
                  <div className="flex items-center gap-1 mb-2">
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs text-muted-foreground">
                      {product.rating} ({product.reviews})
                    </span>
                  </div>
                )}
                
                {/* Price */}
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-base jambo-gradient-text">
                    {formatCurrency(product.price)}
                  </span>
                  {product.original_price && product.original_price > product.price && (
                    <span className="text-xs text-muted-foreground line-through">
                      {formatCurrency(product.original_price)}
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Investments Suggestion (if user showed interest) */}
      {recommendations.investments?.length > 0 && isPersonalized && (
        <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-green-800">Grow Your Wealth</h3>
                <p className="text-sm text-green-600">
                  Start investing from just {formatCurrency(recommendations.investments[0]?.min_investment || 1000)}
                </p>
              </div>
            </div>
            <Button className="bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90">
              Explore Investments <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

// Export tracking function for use in other components
export const trackProductView = async (productId, productType, category, token = null) => {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    await axios.post(`${API_URL}/api/recommendations/track`, {
      product_id: productId,
      product_type: productType,
      category: category,
      action: 'view'
    }, { headers });
  } catch (error) {
    // Silent fail
  }
};

export default PersonalizedRecommendations;
